import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BiMapAlt, BiSolidGridAlt, BiTable } from 'react-icons/bi';

/** The views a list surface can switch between. */
export type ViewModeKey = 'table' | 'tiles' | 'map';

interface ViewModeSwitcherProps<T extends ViewModeKey> {
  value: T;
  onChange: (value: T) => void;
  /** The views on offer, in display order. Fewer than two renders nothing: a switch with one option teaches nothing. */
  options: readonly T[];
  size?: 'small' | 'medium';
}

const OPTION: Record<ViewModeKey, { label: string; icon: React.ReactNode; color: 'utility' | 'primary' }> = {
  table: { label: 'Table', icon: <BiTable style={{ marginRight: 8 }} />, color: 'utility' },
  tiles: { label: 'Tiles', icon: <BiSolidGridAlt style={{ marginRight: 8 }} />, color: 'primary' },
  map: { label: 'Map', icon: <BiMapAlt style={{ marginRight: 8 }} />, color: 'primary' },
};

/** The standard table / tiles / map switch: the first control of a surface's filter set. */
const ViewModeSwitcher = <T extends ViewModeKey>({ value, onChange, options, size = 'medium' }: ViewModeSwitcherProps<T>) => {
  if (options.length < 2) return null;
  return (
    <ToggleButtonGroup
      aria-label='view mode'
      exclusive
      size={size}
      value={value}
      onChange={(_event, next: T | null) => {
        if (next !== null) onChange(next);
      }}
    >
      {options.map(key => (
        <ToggleButton key={key} aria-label={`${OPTION[key].label.toLowerCase()} view`} color={OPTION[key].color} value={key}>
          {OPTION[key].icon}
          {OPTION[key].label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default ViewModeSwitcher;
